import { datadogLogs } from '@datadog/browser-logs';
import { useQuery } from '@tanstack/react-query';
import { SupervisionStatus } from '@web-app/constants/supervision';
import { QUERY_SUPERVISION_TASKS_KEY } from '@web-app/hooks/consts';
import { useApiClient } from '@web-app/hooks/useApiClient';
import { SUPERVISING_STATUS_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';

export interface SupervisionTask {
  appointmentHealthieId: number;
  reviewStatus: SupervisionStatus;
  reviewNotes?: string;
  reviewedAt?: string;
  clientName: string;
  providerName: string;
  appointmentDate: string;
}

export interface SupervisionTasksWithStatusResponse {
  canSupervise: boolean;
  tasks: SupervisionTask[];
}

export interface PaginatedSupervisionTasksResponse {
  canSupervise: boolean;
  tasks: SupervisionTask[];
  hasNextPage: boolean;
  nextCursor?: string;
  totalCount?: number;
}

const getSupervisionTasksWithStatus = async (
  apiClient: ReturnType<typeof useApiClient>,
): Promise<SupervisionTasksWithStatusResponse> => {
  try {
    const response =
      await apiClient.get<SupervisionTasksWithStatusResponse>(
        '/supervision-tasks',
      );

    const data = response.data;

    // If user can't supervise, return the response as-is
    if (!data.canSupervise) {
      return { canSupervise: false, tasks: [] };
    }

    // If user can supervise, return the tasks
    return { canSupervise: true, tasks: data.tasks || [] };
  } catch (error) {
    datadogLogs.logger.error(
      'Unexpected error during supervision tasks with status fetch',
      {
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
        prefix: SUPERVISING_STATUS_LOG_PREFIX,
      },
    );

    // On error, assume user can't supervise
    return { canSupervise: false, tasks: [] };
  }
};

export function useSupervisionTasksWithStatus() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [QUERY_SUPERVISION_TASKS_KEY],
    queryFn: () => getSupervisionTasksWithStatus(apiClient),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  });
}

const getPaginatedSupervisionTasks = async (
  apiClient: ReturnType<typeof useApiClient>,
  status: string,
  cursor?: string,
): Promise<PaginatedSupervisionTasksResponse> => {
  try {
    const url = cursor
      ? `/supervision-tasks?status=${status}&last=${encodeURIComponent(cursor)}`
      : `/supervision-tasks?status=${status}`;

    const response =
      await apiClient.get<PaginatedSupervisionTasksResponse>(url);

    return response.data;
  } catch (error) {
    datadogLogs.logger.error(
      'Unexpected error during paginated supervision tasks fetch',
      {
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
        prefix: SUPERVISING_STATUS_LOG_PREFIX,
        status,
        cursor,
      },
    );

    // On error, return empty result
    return { canSupervise: false, tasks: [], hasNextPage: false };
  }
};

export function usePaginatedSupervisionTasks(status: string, cursor?: string) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [QUERY_SUPERVISION_TASKS_KEY, status, cursor || 'first'],
    queryFn: () => getPaginatedSupervisionTasks(apiClient, status, cursor),
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  });
}
